import React from 'react';
import { Button, Checkbox, FormControlLabel, TextField } from '@material-ui/core';
import ModalCS from '../../../common/ComponentesCS/ModalCS';
import { INTELISIS_ACCOUNT_FIELDS, REQUIRED_INTELISIS_ACCOUNT_FIELDS } from '../constants/intelisisConfig';

const CS_HARDWARE_CATEGORIES = ['Portatiles', 'Desktop', 'Tabletas', 'Servidor', 'Servidores'];
const isHardwareCategoryValue = (categoria, familia) => (
    CS_HARDWARE_CATEGORIES.includes(categoria) || CS_HARDWARE_CATEGORIES.includes(familia)
);
const hasValue = (value) => value !== undefined && value !== null && `${value}`.trim() !== '';
const normalizeComparableValue = (value) => `${value ?? ''}`.trim().toLowerCase();
const MAX_ARTICLE_CODE_LENGTH = 20;
const isWithinMaxLength = (value, maxLength) => !hasValue(value) || `${value}`.length <= maxLength;
const isValidArticleCode = (value) => /^[A-Za-z0-9]+$/.test(`${value ?? ''}`);
const isValidNumericValue = (value) => hasValue(value) && Number.isFinite(Number(value));
const REQUIRED_TEXT_FIELDS = [
    'descripcion1',
    'fabricante',
    'categoria',
    'proveedor',
    'unidad',
    'unidadCompra',
    'familia',
    'monedaPrecio',
    'rama',
    'csmktCategoria'
];
const REQUIRED_SELECT_FIELDS = [
    ...REQUIRED_INTELISIS_ACCOUNT_FIELDS.map(({ valueKey }) => valueKey),
    'intelisis_reteFuente',
    'intelisis_reteICA'
];

const ProductIntelisisConfigDialog = ({
    open,
    selectedProductConfig,
    productConfigDraft,
    onClose,
    onSave,
    onDraftChange,
    onQuantityChange,
    onTouchField,
    cuentasIntelisisOptions = [],
    conceptosReteFuenteOptions = [],
    conceptosReteICAOptions = [],
    hasFieldError,
    intelisisLoading = false,
    getMaxQuantity
}) => {
    const configRowData = selectedProductConfig && productConfigDraft
        ? {
            ...productConfigDraft,
            idFactura: selectedProductConfig.idFactura,
            idArticulo: selectedProductConfig.idArticulo
        }
        : null;

    const duplicateAccountFields = React.useMemo(() => {
        const seenValues = new Map();
        const duplicateFields = new Set();

        INTELISIS_ACCOUNT_FIELDS.forEach(({ field, valueKey }) => {
            const normalizedValue = normalizeComparableValue(productConfigDraft?.[valueKey]);

            if (!normalizedValue) {
                return;
            }

            if (seenValues.has(normalizedValue)) {
                duplicateFields.add(field);
                duplicateFields.add(seenValues.get(normalizedValue));
                return;
            }

            seenValues.set(normalizedValue, field);
        });

        return Array.from(duplicateFields);
    }, [productConfigDraft]);

    const hasDuplicateAccounts = duplicateAccountFields.length > 0;
    const articleValue = productConfigDraft?.articulo !== 'No encontrado' ? productConfigDraft?.articulo : '';
    const hasArticleRequiredError = !hasValue(articleValue);
    const hasArticleFormatError = hasValue(articleValue) && !isValidArticleCode(articleValue);
    const hasArticleLengthError = !isWithinMaxLength(articleValue, MAX_ARTICLE_CODE_LENGTH);
    const hasArticleError = hasArticleRequiredError || hasArticleFormatError || hasArticleLengthError;
    const articleHelperText = hasArticleFormatError
        ? 'Solo se permiten letras sin acentos y numeros, sin espacios ni caracteres especiales.'
        : hasArticleLengthError
            ? `Maximo ${MAX_ARTICLE_CODE_LENGTH} caracteres.`
            : hasArticleRequiredError
                ? 'Campo requerido.'
                : '';
    const nombreCortoValue = productConfigDraft?.nombreCorto !== 'No encontrado' ? productConfigDraft?.nombreCorto : '';
    const hasNombreCortoRequiredError = !hasValue(nombreCortoValue);
    const hasNombreCortoLengthError = !isWithinMaxLength(nombreCortoValue, MAX_ARTICLE_CODE_LENGTH);
    const hasNombreCortoError = hasNombreCortoRequiredError || hasNombreCortoLengthError;
    const nombreCortoHelperText = hasNombreCortoLengthError
        ? `Maximo ${MAX_ARTICLE_CODE_LENGTH} caracteres.`
        : hasNombreCortoRequiredError
            ? 'Campo requerido.'
            : '';
    const impuesto1Value = productConfigDraft?.impuesto1 ?? '';
    const hasImpuesto1RequiredError = !hasValue(impuesto1Value);
    const hasImpuesto1FormatError = hasValue(impuesto1Value) && !isValidNumericValue(impuesto1Value);
    const hasImpuesto1Error = hasImpuesto1RequiredError || hasImpuesto1FormatError;
    const impuesto1HelperText = hasImpuesto1FormatError
        ? 'Debe ser un valor numerico.'
        : hasImpuesto1RequiredError
            ? 'Campo requerido.'
            : '';
    const isHardwareCategory = isHardwareCategoryValue(
        productConfigDraft?.categoria,
        productConfigDraft?.familia
    );
    const hasMissingRequiredTextFields = REQUIRED_TEXT_FIELDS.some((field) => !hasValue(productConfigDraft?.[field]));
    const hasMissingRequiredSelectFields = REQUIRED_SELECT_FIELDS.some((field) => !hasValue(productConfigDraft?.[field]));
    const isSaveDisabled = hasDuplicateAccounts
        || hasArticleError
        || hasNombreCortoError
        || hasImpuesto1Error
        || hasMissingRequiredTextFields
        || hasMissingRequiredSelectFields;

    const renderTextInputField = (label, field, valueKey = field, options = {}) => {
        const currentValue = productConfigDraft?.[valueKey] || '';
        const hasError = configRowData ? hasFieldError(configRowData, field) && !hasValue(currentValue) : false;

        return (
            <TextField
                label={label}
                variant="outlined"
                value={currentValue}
                onChange={(event) => onDraftChange(valueKey, event.target.value)}
                onBlur={() => onTouchField(field)}
                error={hasError}
                helperText={hasError ? 'Campo requerido.' : ''}
                fullWidth
                {...options}
            />
        );
    };

    const renderSelectField = (label, field, valueKey, options, placeholder) => {
        const currentValue = productConfigDraft?.[valueKey];
        const isDuplicateAccount = duplicateAccountFields.includes(field);
        const hasRequiredError = configRowData ? hasFieldError(configRowData, field) && !hasValue(currentValue) : false;
        const hasError = hasRequiredError || isDuplicateAccount;
        const helperText = isDuplicateAccount
            ? 'Esta cuenta no puede repetirse.'
            : hasRequiredError
                ? 'Campo requerido.'
                : '';

        return (
            <div>
                <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>{label}</div>
                <div style={{ minWidth: '180px' }}>
                    <select
                        style={{
                            width: '100%',
                            padding: '5px',
                            border: hasError ? '1px solid #d32f2f' : '1px solid #c4c4c4',
                            borderRadius: '4px',
                            backgroundColor: hasError ? '#fff5f5' : 'white'
                        }}
                        value={currentValue || ''}
                        onChange={(event) => onDraftChange(valueKey, event.target.value)}
                        onBlur={() => onTouchField(field)}
                        disabled={intelisisLoading}
                    >
                        <option value="">{placeholder}</option>
                        {options.map((option, index) => {
                            const value = option.cuenta || option.retencionConcepto || option.codigo || option.value || '';
                            const description = option.descripcion || option.nombre || '';

                            return (
                                <option key={`${field}-${value}-${index}`} value={value}>
                                    {description ? `${value} - ${description}` : value}
                                </option>
                            );
                        })}
                    </select>
                    {hasError && (
                        <div style={{ color: '#d32f2f', fontSize: '11px', marginTop: '4px' }}>
                            {helperText}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <ModalCS
            show={open}
            title={'Configurar articulo'}
            size="lg"
            handleClose={onClose}
        >
            {productConfigDraft && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                        gap: '16px'
                    }}>
                        <TextField
                            label="Factura"
                            variant="outlined"
                            value={productConfigDraft.factura || ''}
                            disabled
                            fullWidth
                        />
                        <TextField
                            label="Articulo"
                            variant="outlined"
                            value={articleValue}
                            onChange={(event) => onDraftChange('articulo', event.target.value)}
                            onBlur={() => onTouchField('articulo')}
                            error={hasArticleError}
                            helperText={articleHelperText}
                            inputProps={{ maxLength: MAX_ARTICLE_CODE_LENGTH }}
                            fullWidth
                        />
                        <TextField
                            label="NombreCorto"
                            variant="outlined"
                            value={nombreCortoValue}
                            onChange={(event) => onDraftChange('nombreCorto', event.target.value)}
                            onBlur={() => onTouchField('nombreCorto')}
                            error={hasNombreCortoError}
                            helperText={nombreCortoHelperText}
                            inputProps={{ maxLength: MAX_ARTICLE_CODE_LENGTH }}
                            fullWidth
                        />
                        <TextField
                            label="Cantidad"
                            variant="outlined"
                            type="number"
                            inputProps={{ min: 0, max: getMaxQuantity(productConfigDraft), step: 1 }}
                            value={productConfigDraft.cantidad ?? ''}
                            onChange={(event) => onQuantityChange(event.target.value)}
                            fullWidth
                        />
                        <TextField
                            label="Precio"
                            variant="outlined"
                            type="number"
                            value={productConfigDraft.importe ?? ''}
                            onChange={(event) => onDraftChange('importe', event.target.value)}
                            fullWidth
                        />
                    </div>

                    {renderTextInputField('Descripcion 1', 'descripcion1', 'descripcion1', {
                        multiline: true,
                        rows: 3
                    })}

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                        gap: '16px'
                    }}>
                        {renderTextInputField('Fabricante', 'fabricante')}
                        {renderTextInputField('Categoria', 'categoria')}
                        {renderTextInputField('Proveedor', 'proveedor')}
                        {renderTextInputField('Unidad', 'unidad')}
                        {renderTextInputField('Unidad Compra', 'unidadCompra')}
                        {renderTextInputField('Familia', 'familia')}
                        {isHardwareCategory && (
                            <TextField
                                label="Procesador"
                                variant="outlined"
                                value={productConfigDraft?.csProcesador || ''}
                                onChange={(event) => onDraftChange('csProcesador', event.target.value)}
                                fullWidth
                            />
                        )}
                        {isHardwareCategory && (
                            <TextField
                                label="Sistema Operativo"
                                variant="outlined"
                                value={productConfigDraft?.csSistemaOperativo || ''}
                                onChange={(event) => onDraftChange('csSistemaOperativo', event.target.value)}
                                fullWidth
                            />
                        )}
                        {renderTextInputField('Moneda Precio', 'monedaPrecio')}
                        {renderTextInputField('Rama', 'rama')}
                        {renderTextInputField('CSMKT Categoria', 'csmktCategoria')}
                        <TextField
                            label="Impuesto 1"
                            variant="outlined"
                            type="number"
                            value={impuesto1Value}
                            onChange={(event) => onDraftChange('impuesto1', event.target.value)}
                            onBlur={() => onTouchField('impuesto1')}
                            error={hasImpuesto1Error}
                            helperText={impuesto1HelperText}
                            fullWidth
                        />
                        <FormControlLabel
                            control={(
                                <Checkbox
                                    color="primary"
                                    checked={Boolean(productConfigDraft.impuesto1Excento)}
                                    onChange={(event) => onDraftChange('impuesto1Excento', event.target.checked)}
                                    onBlur={() => onTouchField('impuesto1Excento')}
                                />
                            )}
                            label="Impuesto 1 excento"
                        />
                        <FormControlLabel
                            control={(
                                <Checkbox
                                    color="primary"
                                    checked={Boolean(productConfigDraft.fiscalIngresoTercero)}
                                    onChange={(event) => onDraftChange('fiscalIngresoTercero', event.target.checked)}
                                    onBlur={() => onTouchField('fiscalIngresoTercero')}
                                />
                            )}
                            label="Fiscal ingreso tercero"
                        />
                    </div>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                        gap: '16px'
                    }}>
                        {INTELISIS_ACCOUNT_FIELDS.map(({ label, field, valueKey, placeholder }) => (
                            <React.Fragment key={field}>
                                {renderSelectField(
                                    label,
                                    field,
                                    valueKey,
                                    cuentasIntelisisOptions,
                                    placeholder
                                )}
                            </React.Fragment>
                        ))}
                        {renderSelectField(
                            'Retencion Fuente',
                            'reteFuente',
                            'intelisis_reteFuente',
                            conceptosReteFuenteOptions,
                            'Seleccionar concepto...'
                        )}
                        {renderSelectField(
                            'Retencion ICA',
                            'reteICA',
                            'intelisis_reteICA',
                            conceptosReteICAOptions,
                            'Seleccionar concepto...'
                        )}
                    </div>

                    {hasDuplicateAccounts && (
                        <div style={{
                            backgroundColor: '#fdecea',
                            border: '1px solid #f5c6cb',
                            padding: '10px',
                            borderRadius: '5px',
                            color: '#b71c1c'
                        }}>
                            Las cuentas Intelisis capturadas deben ser diferentes entre si.
                        </div>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                        <Button variant="outlined" onClick={onClose}>
                            Cancelar
                        </Button>
                        <Button variant="contained" color="primary" onClick={onSave} disabled={isSaveDisabled}>
                            Guardar configuracion
                        </Button>
                    </div>
                </div>
            )}
        </ModalCS>
    );
};

export default ProductIntelisisConfigDialog;
